/**
 * @fileoverview One tick-or-cross cell of the capability matrix.
 * @description Shows whether a role holds a permission as a green "Yes" or red "No" cell. While
 * editing, a cell of an editable role and active permission becomes a button that flips it, and a
 * cell with an unsaved change gets a blue ring. A Super Admin cell is fixed, and an inactive
 * permission shows a muted dash because nobody can be given it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import type { TFunction } from 'i18next'

interface CapabilityCellProps {
  roleCode: string
  permissionCode: string
  /** Whether the role holds the permission (through the draft while editing). */
  granted: boolean
  /** True for Super Admin's column. */
  locked: boolean
  /** True when the permission is active, i.e. can be granted. */
  active: boolean
  /** True while the page is in edit mode. */
  editing: boolean
  /** True when the edit draft changes this cell. */
  changed: boolean
  onToggle: (roleCode: string, permissionCode: string) => void
}

const CELL_CLASS_NAMES = 'flex h-full min-h-11 w-full items-center justify-center gap-1.5'

/**
 * @description Writes a cell's tooltip: whether the role holds the permission, plus what a click
 * does when the cell is editable.
 * @param {TFunction} t - The translate function.
 * @param {{ granted: boolean; locked: boolean; live: boolean }} state - The cell's state.
 * @returns {string} The tooltip.
 */
function describeCell(
  t: TFunction,
  { granted, locked, live }: { granted: boolean; locked: boolean; live: boolean },
): string {
  let held = t('capabilities.cell.doesNotHold')
  if (locked) {
    held = t('capabilities.cell.superAdminAlways')
  } else if (granted) {
    held = t('capabilities.cell.holds')
  }

  if (!live) {
    return held
  }

  return t('capabilities.cell.tipWithAction', {
    state: held,
    action: granted ? t('capabilities.cell.clickToRevoke') : t('capabilities.cell.clickToGrant'),
  })
}

/**
 * @component
 * @param {{ granted: boolean }} props - Whether the cell shows a tick (Yes) or a cross (No).
 */
function CellMark({ granted }: { granted: boolean }) {
  const { t } = useTranslation()

  return (
    <>
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {granted ? (
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        ) : (
          <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
        )}
      </svg>
      {granted ? t('capabilities.cell.yes') : t('capabilities.cell.no')}
    </>
  )
}

/**
 * @component
 * @param {CapabilityCellProps} props - The cell's state and its toggle handler.
 * @param {string} props.roleCode - The role this cell's column is for.
 * @param {string} props.permissionCode - The permission this cell's row is for.
 * @param {boolean} props.granted - Whether the role holds the permission.
 * @param {boolean} props.locked - Whether the role's column is fixed (Super Admin).
 * @param {boolean} props.active - Whether the permission can be granted at all.
 * @param {boolean} props.editing - Whether the page is in edit mode.
 * @param {boolean} props.changed - Whether the cell has an unsaved change.
 * @param {(roleCode: string, permissionCode: string) => void} props.onToggle - Called with this
 * cell's role and permission codes when an editable cell is clicked.
 */
function CapabilityCell({
  roleCode,
  permissionCode,
  granted,
  locked,
  active,
  editing,
  changed,
  onToggle,
}: CapabilityCellProps) {
  const { t } = useTranslation()
  const testId = `capability-cell-${roleCode}-${permissionCode}`

  if (!active) {
    return (
      <td
        className="h-11 bg-surface-muted p-0 text-center text-footer"
        title={t('capabilities.cell.inactive')}
        data-testid={testId}
      >
        <span className="text-[12.5px] font-semibold">–</span>
      </td>
    )
  }

  const live = editing && !locked
  const tone = granted ? 'bg-success-bg text-track' : 'bg-danger-bg text-danger'
  const state = `${tone} ${locked ? 'opacity-80' : ''} ${changed ? 'ring-2 ring-inset ring-primary' : ''}`

  return (
    <td
      className={`h-11 p-0 text-center text-[12.5px] font-semibold ${state}`}
      title={describeCell(t, { granted, locked, live })}
      data-testid={testId}
      data-granted={granted}
      data-changed={changed}
    >
      {live ? (
        <button
          type="button"
          onClick={() => onToggle(roleCode, permissionCode)}
          aria-pressed={granted}
          data-testid={`capability-toggle-${roleCode}-${permissionCode}`}
          className={`${CELL_CLASS_NAMES} cursor-pointer`}
        >
          <CellMark granted={granted} />
        </button>
      ) : (
        <span className={CELL_CLASS_NAMES}>
          <CellMark granted={granted} />
        </span>
      )}
    </td>
  )
}

export default CapabilityCell
